import { Card, CardContent, CardHeader, Container, Grid, styled, Typography } from '@material-ui/core'
import { FC, useEffect, useState } from 'react'
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import useLoading from '../../../../control/loading/useLoading'
import useTranslate from '../../../../i18n/useTranslate'
import { RoutePath } from '../../../../router/routesConfig'
import { ConsiderHistoryInfo } from '../../../../services/web/types/responseWebServiceTypes'
import useWebService from '../../../../services/web/useWebService'
import { convertDateToFormatEN, convertDateToFormatTH } from '../../../../utilities/date'
import { decryptionAES, encryptionAES } from '../../../../utilities/encryption'
import { ButtonClose } from '../../project/page/pageAction'

type MatchParams = {
    projectData: string
}

const CustomHeader = styled(CardHeader)({
    borderBottom: '1px solid #DCDCDC',
})

const CustomUnderlineGrid = styled(Grid)({
    borderBottom: '1px solid #DCDCDC',
})

const ConsiderTypeHistory: FC<{}> = () => {
    const translate = useTranslate()
    const webServiceHook = useWebService()
    const history = useHistory()
    const matchPath = useRouteMatch<MatchParams>(RoutePath.considerTypeHistory)
    const encryptedProjectData = matchPath ? matchPath.params.projectData : null
    const projectData = encryptedProjectData ? JSON.parse(decryptionAES(encryptedProjectData)) : null
    const { projectID, submittedID } = projectData
    const { state } = useLocation()
    const [historyInfo, setHistoryInfo] = useState<ConsiderHistoryInfo>()
    const loadingHook = useLoading()

    const initialData = async () => {
        loadingHook.loadingOpen()
        const response = await webServiceHook.getConsiderTypeHistory(submittedID)
        if (response.data && response.data?.status === 1) {
            setHistoryInfo(response.data.result)
        }
        loadingHook.loadingClose()
    }

    const renderLanguages = (data: any, thKey: string, enKey: string) => {
        return translate.languageStore.currentLanguage === 'th' ? data?.[thKey] : data?.[enKey]
    }

    const renderDate = (data: any, key: string) => {
        let formatDate = '-'
        const isTH = translate.languageStore.currentLanguage === 'th'
        if (isTH && data?.[key]) {
            formatDate = convertDateToFormatTH(data?.[key])
        } else if (!isTH && data?.[key]) {
            formatDate = convertDateToFormatEN(data?.[key])
        }
        return formatDate
    }

    useEffect(() => {
        initialData()
    }, [])

    const handleClose = () => {
        const encryptedProjectID = encryptionAES(projectID)
        const encryptedSubmittedID = encryptionAES(submittedID)

        const path = generatePath(RoutePath.manageProjectEdit, {
            projectID: encryptedProjectID,
            submittedID: encryptedSubmittedID,
        })

        history.push({
            pathname: path,
            state: state,
        })
    }

    return (
        <Container maxWidth="lg">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CustomHeader
                            title={<Typography variant="h5">{translate.get('LABEL.CONSIDER_TYPE_HISTORY')}</Typography>}
                        />
                        <CardContent style={{ padding: '24px' }}>
                            {(historyInfo?.modifiedInfo ?? []).map((data, index) => (
                                <Grid container key={data.historyID} spacing={2}>
                                    {index !== 0 && (
                                        <CustomUnderlineGrid item xs={12}>
                                            &nbsp;
                                        </CustomUnderlineGrid>
                                    )}
                                    <Grid item xs={12}>
                                        { index !== 0 && <pre />}
                                        <Typography variant="h5">
                                            {translate.get('LABEL.RESULT_TIMES', [`${data.rowNo}`])}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={6}>
                                        <Typography variant="body2">
                                            {translate.get('LABEL.PROJECT_TYPE')} :{' '}
                                            <span style={{ color: data.considerColor, fontWeight: 'bolder' }}>
                                                {renderLanguages(data, 'considerNameTH', 'considerNameEN') ?? '-'}
                                            </span>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={6}>
                                        <Typography variant="body2">
                                            {translate.get('LABEL.CHANGED_DATE')} : {renderDate(data, 'updatedDate')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2">
                                            {translate.get('LABEL.FILE_UPLOAD_BY')} :{' '}
                                            {renderLanguages(data, 'updatedNameTH', 'updatedNameEN') ?? '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Grid container justify="flex-end">
                        <ButtonClose onClickClose={handleClose}></ButtonClose>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

export default ConsiderTypeHistory
